/**
 * KYB submission types, validation, and the two API helpers (document mint
 * + case submit). Mirrors the backend DTOs under src/modules/dashboard/dto:
 * when the backend adds a doc kind or a company type, both files move
 * together.
 */

import { api, ApiError } from '~/lib/api';

// ---------- types that mirror the backend DTOs ----------

export const COMPANY_TYPES = ['RC', 'BN', 'IT', 'LP', 'LLP'] as const;
export type CompanyType = (typeof COMPANY_TYPES)[number];

export const COMPANY_TYPE_LABEL: Record<CompanyType, string> = {
  RC: 'RC — Registered Company',
  BN: 'BN — Business Name',
  IT: 'IT — Incorporated Trustee',
  LP: 'LP — Limited Partnership',
  LLP: 'LLP — Limited Liability Partnership',
};

export const DIRECTOR_ROLES = [
  'director',
  'shareholder',
  'secretary',
  'other',
] as const;
export type DirectorRole = (typeof DIRECTOR_ROLES)[number];

export const DIRECTOR_ROLE_LABEL: Record<DirectorRole, string> = {
  director: 'Director',
  shareholder: 'Shareholder',
  secretary: 'Secretary',
  other: 'Other',
};

export const DOCUMENT_KINDS = [
  'cac_certificate',
  'memorandum_of_association',
] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

export const DOCUMENT_LABEL: Record<DocumentKind, string> = {
  cac_certificate: 'CAC Certificate',
  memorandum_of_association: 'Memorandum & Articles of Association',
};

export const ACCEPTED_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
] as const;

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB — matches the preflight the backend accepts in practice

// ---------- form state ----------

export interface DirectorDraft {
  firstName: string;
  lastName: string;
  role: DirectorRole;
  nationality: string;
  bvn: string;
  email: string;
}

export type UploadState =
  | { kind: 'empty' }
  | { kind: 'uploading'; fileName: string; progress: number }
  | { kind: 'uploaded'; fileName: string; documentId: string }
  | { kind: 'failed'; fileName: string; message: string };

export interface KybDraft {
  // Business
  appliedCompanyName: string;
  businessDescription: string;
  companyType: CompanyType | '';
  rcNumber: string;
  tin: string;
  // Address
  addressLine: string;
  state: string;
  city: string;
  contactPhone: string;
  contactEmail: string;
  // Leadership
  directors: DirectorDraft[];
  // Documents
  documents: Record<DocumentKind, UploadState>;
}

export function emptyDirector(): DirectorDraft {
  return {
    firstName: '',
    lastName: '',
    role: 'director',
    nationality: 'Nigerian',
    bvn: '',
    email: '',
  };
}

export function emptyDraft(): KybDraft {
  return {
    appliedCompanyName: '',
    businessDescription: '',
    companyType: '',
    rcNumber: '',
    tin: '',
    addressLine: '',
    state: '',
    city: '',
    contactPhone: '',
    contactEmail: '',
    directors: [emptyDirector()],
    documents: {
      cac_certificate: { kind: 'empty' },
      memorandum_of_association: { kind: 'empty' },
    },
  };
}

// ---------- validation ----------

export type Errors = Partial<Record<string, string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BVN_RE = /^\d{11}$/;
const RC_RE = /^[A-Za-z0-9-]{1,20}$/;
const PHONE_RE = /^[+0-9 \-()]{6,32}$/;

export function validateBusiness(d: KybDraft): Errors {
  const e: Errors = {};
  if (d.appliedCompanyName.trim().length < 1) e.appliedCompanyName = 'Required';
  if (d.businessDescription.trim().length < 1)
    e.businessDescription = 'Describe what the business does';
  else if (d.businessDescription.length > 2000)
    e.businessDescription = 'Max 2000 characters';
  if (!d.companyType) e.companyType = 'Pick one';
  if (!RC_RE.test(d.rcNumber.trim()))
    e.rcNumber = 'Alphanumeric, up to 20 characters';
  if (d.tin.trim().length < 1) e.tin = 'Required';
  return e;
}

export function validateAddress(d: KybDraft): Errors {
  const e: Errors = {};
  if (d.addressLine.trim().length < 1) e.addressLine = 'Required';
  if (d.state.trim().length < 1) e.state = 'Pick a state';
  if (d.city.trim().length < 1) e.city = 'Required';
  if (!PHONE_RE.test(d.contactPhone.trim()))
    e.contactPhone = 'Looks like a phone number: +234…';
  if (!EMAIL_RE.test(d.contactEmail.trim()))
    e.contactEmail = 'Needs a valid email';
  return e;
}

export function validateDirectors(d: KybDraft): Errors {
  const e: Errors = {};
  if (d.directors.length < 1) {
    e.directors = 'At least one director is required';
    return e;
  }
  d.directors.forEach((dir, i) => {
    if (dir.firstName.trim().length < 1) e[`directors.${i}.firstName`] = 'Required';
    if (dir.lastName.trim().length < 1) e[`directors.${i}.lastName`] = 'Required';
    if (dir.nationality.trim().length < 2)
      e[`directors.${i}.nationality`] = 'Required';
    if (!BVN_RE.test(dir.bvn.trim())) e[`directors.${i}.bvn`] = 'BVN is 11 digits';
    if (!EMAIL_RE.test(dir.email.trim()))
      e[`directors.${i}.email`] = 'Needs a valid email';
  });
  return e;
}

export function validateDocuments(d: KybDraft): Errors {
  const e: Errors = {};
  for (const kind of DOCUMENT_KINDS) {
    if (d.documents[kind].kind !== 'uploaded') {
      e[`documents.${kind}`] = 'Upload required';
    }
  }
  return e;
}

export function hasErrors(e: Errors): boolean {
  return Object.keys(e).length > 0;
}

// ---------- API helpers ----------

export interface MintedSlot {
  documentId: string;
  uploadUrl: string;
  expiresAt: string;
}

export async function mintDocumentSlot(
  kind: DocumentKind,
  contentType: string,
  sizeBytes: number,
): Promise<MintedSlot> {
  return api<MintedSlot>('/dashboard/kyb/documents', {
    method: 'POST',
    body: { kind, contentType, sizeBytes },
  });
}

/**
 * Direct PUT of the file bytes to the Spaces presigned URL. MUST NOT send
 * cookies — the URL already carries its own signature, and sending the
 * session cookie to a non-API host would be a leak.
 */
export async function uploadToSpaces(
  uploadUrl: string,
  file: File,
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'content-type': file.type },
  });
  if (!res.ok) {
    throw new Error(`Upload failed (${res.status}). Try again in a moment.`);
  }
}

export async function submitKyb(
  draft: KybDraft,
): Promise<{ caseId: string }> {
  if (!draft.companyType) {
    throw new Error('companyType must be chosen before submit');
  }
  const documentIds: string[] = [];
  for (const kind of DOCUMENT_KINDS) {
    const state = draft.documents[kind];
    if (state.kind !== 'uploaded') {
      throw new Error(`${DOCUMENT_LABEL[kind]} is not uploaded`);
    }
    documentIds.push(state.documentId);
  }
  return api<{ caseId: string }>('/dashboard/kyb', {
    method: 'POST',
    body: {
      appliedCompanyName: draft.appliedCompanyName.trim(),
      businessDescription: draft.businessDescription.trim(),
      companyType: draft.companyType,
      rcNumber: draft.rcNumber.trim(),
      tin: draft.tin.trim(),
      addressLine: draft.addressLine.trim(),
      state: draft.state.trim(),
      city: draft.city.trim(),
      contactPhone: draft.contactPhone.trim(),
      contactEmail: draft.contactEmail.trim(),
      directors: draft.directors.map((d) => ({
        firstName: d.firstName.trim(),
        lastName: d.lastName.trim(),
        role: d.role,
        nationality: d.nationality.trim(),
        bvn: d.bvn.trim(),
        email: d.email.trim(),
      })),
      documentIds,
    },
  });
}

/** Surfaces either the Nest error-envelope message or a plain string. */
export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}
